import { isEmpty } from '@ember/utils';
import { tracked } from '@glimmer/tracking';
import { DataActionConditionType, CreateEventComposerSection } from 'cio/utils/campaigns/data-action';
import { stringToPath, keyPathToString } from 'cio/utils/objects';

interface BaseParsedAPIRecipientCondition {
  field: string;
  type: DataActionConditionType;
}

interface ParsedAPIRecipientConditionTriggerAttribute extends BaseParsedAPIRecipientCondition {
  type: DataActionConditionType.TriggerAttribute;
  value?: string;
  path?: (string | number)[];
}

interface ParsedAPIRecipientConditionProfileAttribute extends BaseParsedAPIRecipientCondition {
  type: DataActionConditionType.ProfileAttribute;
  value?: string;
  path?: (string | number)[];
}

interface ParsedAPIRecipientConditionEventAttribute extends BaseParsedAPIRecipientCondition {
  type: DataActionConditionType.EventAttribute;
  value?: string;
  path?: (string | number)[];
}

interface ParsedAPIRecipientConditionLiquid extends BaseParsedAPIRecipientCondition {
  type: DataActionConditionType.Liquid;
  value: string;
}

interface ParsedAPIRecipientConditionStatic extends BaseParsedAPIRecipientCondition {
  type: DataActionConditionType.Static;
  value: string;
}

export type ParsedAPIRecipientCondition =
  | ParsedAPIRecipientConditionTriggerAttribute
  | ParsedAPIRecipientConditionProfileAttribute
  | ParsedAPIRecipientConditionEventAttribute
  | ParsedAPIRecipientConditionLiquid
  | ParsedAPIRecipientConditionStatic;

const DEFAULT_OPTIONS = {
  defaultType: DataActionConditionType.TriggerAttribute,
  mustMatchRecipient: false,
};

export default class DataActionRecipientCondition implements CreateEventComposerSection {
  // mustMatchRecipient tells us whether a recipient condition must be set. i.e.
  // in data campaigns, there needs to be a condition to find a person to send
  // the event to.
  // In a people-based campaign, there is no need for such a condition - the
  // event can be sent to the person who's journey this is.
  mustMatchRecipient = true;

  // In a people-based campaign, this controls whether the user chose to send
  // the event to the person in the workflow, or whether they want to match a
  // recipient using a condition
  @tracked shouldMatchRecipient = false;

  @tracked field = '';
  @tracked type: DataActionConditionType = DataActionConditionType.TriggerAttribute;
  @tracked value = '';
  @tracked path: (string | number)[] = [];

  originalShouldMatchRecipient = false;

  originalField = '';
  originalType = DataActionConditionType.TriggerAttribute;
  originalValue = '';
  originalPath: (string | number)[] = [];

  constructor(
    options: {
      parsedCondition?: ParsedAPIRecipientCondition;
      defaultType?: DataActionConditionType;
      mustMatchRecipient?: boolean;
    } = {},
  ) {
    let _options = {
      ...DEFAULT_OPTIONS,
      ...options,
    };

    if (_options.defaultType != null) {
      this.type = _options.defaultType;
    }

    if (_options.mustMatchRecipient != null) {
      this.mustMatchRecipient = _options.mustMatchRecipient;
    }

    if (this.mustMatchRecipient) {
      this.shouldMatchRecipient = true;
    } else {
      this.shouldMatchRecipient = _options.parsedCondition != null;
    }

    if (_options.parsedCondition != null) {
      let { parsedCondition } = _options;

      this.field = parsedCondition.field;
      this.type = parsedCondition.type;

      if (
        parsedCondition.type === DataActionConditionType.Liquid ||
        parsedCondition.type === DataActionConditionType.Static
      ) {
        this.value = parsedCondition.value;
      } else {
        if (parsedCondition.value != null) {
          this.path = stringToPath(parsedCondition.value);
          this.value = parsedCondition.value;
        } else if (parsedCondition.path != null) {
          this.path = parsedCondition.path;
          this.value = keyPathToString(this.path);
        }
      }
    }

    this.originalField = this.field;
    this.originalType = this.type;
    this.originalValue = this.value;
    this.originalPath = this.path;
    this.originalShouldMatchRecipient = this.shouldMatchRecipient;
  }

  static deserialize(
    condition: string,
    options: {
      defaultType?: DataActionConditionType;
      mustMatchRecipient?: boolean;
    } = {},
  ) {
    if (isEmpty(condition)) {
      return new DataActionRecipientCondition(options);
    }

    try {
      let parsedCondition = JSON.parse(condition);
      return new DataActionRecipientCondition({
        parsedCondition,
        ...options,
      });
    } catch {
      return new DataActionRecipientCondition(options);
    }
  }

  serialize() {
    if (!this.mustMatchRecipient && !this.shouldMatchRecipient) {
      return '';
    }

    if (isEmpty(this.field)) {
      return '';
    }

    return JSON.stringify({
      field: this.field,
      type: this.type,
      value: this.value.trim(),
    });
  }

  get isDirty() {
    let hasShouldMatchRecipientChanged = this.shouldMatchRecipient !== this.originalShouldMatchRecipient;

    if (!this.shouldMatchRecipient) {
      return hasShouldMatchRecipientChanged;
    }

    return (
      hasShouldMatchRecipientChanged ||
      this.field !== this.originalField ||
      this.type !== this.originalType ||
      this.value !== this.originalValue
    );
  }

  get isValid() {
    if (!this.shouldMatchRecipient) {
      return true;
    }

    return !isEmpty(this.field) && !isEmpty(this.value.trim());
  }
}
